import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom"
import { setModal, selectModal } from "../../../redux/modalSlice";
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import LoxSelect from '../../../components/LoxSelect'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { selectPaymentScheduleData } from "../../../redux/paymentScheduleDataSlice"
import { format } from 'date-fns';
import { DateFormat, CurrencyFormat } from '../../../helpers/formatting';
import Loading from '../../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

export default function EditPaymentModal() {
  const dispatch = useDispatch();
  const params = useParams()
  const modal = useSelector(selectModal);
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const paymentScheduleData = useSelector(selectPaymentScheduleData)
  const allowedPaymentStatuses = [
    'payment_status_scheduled',
    'payment_status_rescheduled',
    'payment_status_paid',
    'payment_status_returned',
    'payment_status_hold',
    'payment_status_chargeback',
    'payment_status_missed',
  ];

  const paymentReturnCode = dropdowns.data.filter((o) => o.code_key === 'payment_return_code')[0]
  const paymentReturnCodeOptions = dropdownOptions.filter((o) => o.dropdown_id === paymentReturnCode.id).map((o) => ({ label: o.name, value: o.id }))
  const paymentStatus = dropdowns.data.filter((o) => o.code_key === 'payment_status')[0]
  const paymentStatusOptions = dropdownOptions.filter((o) => o.dropdown_id === paymentStatus.id && allowedPaymentStatuses.includes(o.code_key)).map((o) => ({ label: o.name, value: o.id }))
  const paymentMethod = dropdowns.data.filter((o) => o.code_key === 'payment_method')[0]
  const paymentMethodOptions = dropdownOptions.filter((o) => o.dropdown_id === paymentMethod.id).map((o) => ({ label: o.name, value: o.id }))
  let tempPaymentMethodOptions = []

  const { register, handleSubmit, control, watch, setValue, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const loanId = modal.options?.loanId
  const paymentId = modal.options?.paymentId
  const [paymentInfo, setPaymentInfo] = useState({})
  const [disableFields, setDisableFields] = useState(false)
  const [totalAmountWarningText, setTotalAmountWarningText] = useState("")
  const [dateWarningText, setDateWarningText] = useState("")
  const [receivedDate, setReceivedDate] = useState("")
  const [postedDate, setPostedDate] = useState("")
  const [returnPostedDate, setReturnPostedDate] = useState("")
  const [postMarkedDate, setPostMarkedDate] = useState("")
  const [availablePaymentMethods, setavailablePaymentMethods] = useState("")
  const customerId = params?.id
  const [customerInfo, setCustomerInfo] = useState(null)
  const paymentMethodHelperText = modal.options?.paymentMethodHelperText
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;

  const inFlightPaymentId = useRef(null)

  const getCustomerInformation = async (customerId, forPaymentId) => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/ach/status`)
    try {
      const response = await request.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightPaymentId.current !== forPaymentId) return
      if (response.status === 200 || response.status === 201) {
        setCustomerInfo(response?.data?.data)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const getAvailablePaymentMethods = async (forPaymentId) => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}`)
    try {
      const response = await request.get();
      if (inFlightPaymentId.current !== forPaymentId) return
      tempPaymentMethodOptions = response?.data?.data?.payment_method_dropdown_options?.map((o) => ({ label: o.name, value: o.id }))
      setavailablePaymentMethods(tempPaymentMethodOptions)
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  useEffect(() => {
    if (modal.options?.paymentId && modal.type === 'payment_schedule_edit_payment_modal') {
      // This modal stays mounted in the Footer; clear the previous payment's values and the previous
      // customer's ACH status before loading this payment.
      resetState()
      inFlightPaymentId.current = paymentId
      getCustomerInformation(customerId, paymentId)
      getAvailablePaymentMethods(paymentId)
      let payment = paymentScheduleData.paymentScheduleData?.payments.find((o) => o.id === modal.options?.paymentId)
      setPaymentInfo(payment)
      if (payment) {
        setValue("submitted_date", payment.submitted_date || "");
        setValue("received_date", payment.received_date || "");
        setValue("postmarked_date", payment.postmarked_date || "");
        setValue("actual_amount", payment.actual_amount || "");
        setValue("description", payment.description || "");
        setValue("returned_date", payment.returned_date || "");
        setValue("payment_return_code_dropdown_option_id", paymentReturnCodeOptions.find((opt) => opt.value === payment.payment_return_code_dropdown_option_id));
        setValue("check_number", payment.check_number || "");
        setValue("payment_status_dropdown_option_id", paymentStatusOptions.find((opt) => opt.value === payment.payment_status_dropdown_option_id));
        setValue("payment_method_dropdown_option_id", paymentMethodOptions.find((opt) => opt.value === payment.payment_method_dropdown_option_id));

        setDisableFields(dropdownOptions.find((o) => o?.id === payment.payment_method_dropdown_option_id && o.optional_value_3 === 'FALSE') ? false : true);

        processTotalAmountWarningText(payment.actual_amount || "", payment.scheduled_amount)
        setReceivedDate(payment.received_date || "")
        setPostedDate(payment.posted_date || "")
        setPostMarkedDate(payment.postmarked_date || "")
        setReturnPostedDate(payment.return_posted_date || "")
      }
    }
  }, [modal.options?.paymentId])

  const resetState = () => {
    reset()
    setSaving(false)
    setavailablePaymentMethods([])
    setCustomerInfo(null)
    setPaymentInfo({})
    setDisableFields(false)
    setTotalAmountWarningText("")
    setDateWarningText("")
    setReceivedDate("")
    setPostedDate("")
    setReturnPostedDate("")
    setPostMarkedDate("")
  }

  const handleCancel = () => {
    inFlightPaymentId.current = null
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  const processTotalAmountWarningText = (totalAmount, scheduled_amount) => {
    if (totalAmount === "") {
      setTotalAmountWarningText("");
    } else {
      let warningText = "";
      let reflectAmount = 0;
      if (totalAmount > +scheduled_amount) {
        reflectAmount = totalAmount - scheduled_amount;
        warningText = "Warning: The entered amount is more than the scheduled amount. A paydown will be created for " + CurrencyFormat({ value: reflectAmount }) + " to accurately allocate the payment.";
      } else if (totalAmount < +scheduled_amount) {
        warningText = "Warning: The entered amount is less than the scheduled amount. This modification will not be reflected in the currently edited payment";
      }
      setTotalAmountWarningText(warningText);
    }
  }

  useEffect(() => {
    if (modal.options?.paymentId) {
      setDateWarningText("")

      const received = receivedDate ? new Date(receivedDate) : null;
      const posted = postedDate ? new Date(postedDate) : null;
      const postMarked = postMarkedDate ? new Date(postMarkedDate) : null;

      const dates = [received, posted, postMarked].filter((date) => date);

      if (dates.length > 0) {
        const minDate = new Date(Math.min.apply(null, dates));
        if (minDate < new Date(paymentInfo?.scheduled_date)) {
          setDateWarningText("");
        } else {
          setDateWarningText("Warning: this payment will be considered late");
        }
      }
    }
  }, [receivedDate, postedDate, postMarkedDate]);

  const onSubmit = async (formData) => {
    const paymentMethodOption = formData.payment_method_dropdown_option_id?.value;
    if (paymentMethodACHId == paymentMethodOption && customerInfo?.ach_revocation_active) {
      dispatch(
        setModal({
          is_active: true,
          type: 'allow_ach_payments_modal',
          options: { customerId: customerId, hasBkConfirmed: customerInfo?.has_bk_confirmed }
        })
      )
    } else {
      setSaving(true)
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}`)
      try {
        let body = {}

        if (disableFields) {
          body.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id?.value
          body.submitted_date = formData?.submitted_date ? format(new Date(formData.submitted_date), 'yyyy-MM-dd HH:mm:ss') : null
        } else {
          body.payment_return_code_dropdown_option_id = formData.payment_return_code_dropdown_option_id?.value
          body.payment_status_dropdown_option_id = formData.payment_status_dropdown_option_id?.value
          body.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id?.value
          body.received_date = formData?.received_date ? format(new Date(formData.received_date), 'yyyy-MM-dd HH:mm:ss') : null
          body.returned_date = formData?.returned_date ? format(new Date(formData.returned_date), 'yyyy-MM-dd HH:mm:ss') : null
          body.actual_amount = formData.actual_amount
          body.description = formData.description
          body.postmarked_date = formData?.postmarked_date ?? null
          body.check_number = formData.check_number
        }

        if (paymentScheduleData.paymentScheduleCurrentDate !== null) {
          body.current_date = paymentScheduleData.paymentScheduleCurrentDate
        }

        const response = await request.modify(body)
        if (response.status === 200 || response.status === 201) {
          modal.options.successCallback()
          handleCancel()
          dispatch(setNotification({ type: 'success', body: '' }))
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  };

  return modal.is_active && modal.type === 'payment_schedule_edit_payment_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="payment_schedule_edit_payment_modal_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-6" data-testid="edit-payment-modal">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized" style={{ maxWidth: "100%" }}>
              Edit Payment #{paymentInfo?.id}<span className="ml-5">Scheduled Date: {paymentInfo?.scheduled_date && <DateFormat dateString={paymentInfo?.scheduled_date} />}</span><span className="ml-5">Amount: <CurrencyFormat value={paymentInfo?.scheduled_amount} showCurrencySign /></span>
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "50px", overflow: 'hidden' }}
          >
            {availablePaymentMethods.length == 0 && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}
            {availablePaymentMethods.length > 0 && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="submitted_date">Submitted Date:</label>
                    <Controller
                      name="submitted_date"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <input {...field} type="datetime-local" id="submitted_date" className="input is-small" disabled={!disableFields} />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="received_date">Received Date:</label>
                    <Controller
                      name="received_date"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <input
                          {...field}
                          data-testid="edit-payment-modal-received-date"
                          type="datetime-local"
                          id="received_date"
                          className="input is-small"
                          disabled={disableFields}
                          onChange={(val) => {
                            setReceivedDate(val.target.value)
                            field.onChange(val)
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">

                    <label className={`edit-payment-modal-label`} htmlFor="description">Posted Date:</label>
                    <input
                      id="postedDate"
                      name="postedDate"
                      className="input is-small"
                      type="text"
                      disabled
                      placeholder=""
                      value={postedDate}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="postmarked_date">Postmarked Date:</label>
                    <Controller
                      name="postmarked_date"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <input
                          {...field}
                          data-testid="edit-payment-modal-postmarked-date"
                          type="date"
                          id="postmarked_date"
                          className="input is-small"
                          disabled={disableFields}
                          onChange={(val) => {
                            setPostMarkedDate(val.target.value)
                            field.onChange(val)
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="actual_amount" />
                    <p className="has-text-danger" style={{ fontSize: "12px", width: "100%" }}>{dateWarningText}</p>
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="actual_amount">Actual Amount:</label>
                    <Controller
                      name="actual_amount"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <input
                          {...field}
                          data-testid="edit-payment-modal-actual-amount"
                          type="number"
                          id="actual_amount"
                          className="input is-small"
                          disabled={disableFields}
                          step="0.01"
                          onChange={(val) => {
                            processTotalAmountWarningText(val.target.value, paymentInfo?.scheduled_amount)
                            field.onChange(val)
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="actual_amount" />
                    <p className="has-text-danger" style={{ fontSize: "12px", width: "100%" }}>{!disableFields && totalAmountWarningText}</p>
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="description">Description:</label>
                    <Controller
                      name="description"
                      control={control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <input {...field} type="text" id="description" className="input is-small" disabled={disableFields} />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="returned_date">Returned Date:</label>
                    <Controller
                      name="returned_date"
                      control={control}
                      render={({ field }) => (
                        <input {...field} type="datetime-local" id="returned_date" className="input is-small" disabled={disableFields} />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">

                    <label className={`edit-payment-modal-label`} htmlFor="description">Return Posted Date:</label>
                    <input
                      id="returnPostedDate"
                      name="returnPostedDate"
                      className="input is-small"
                      type="text"
                      disabled
                      placeholder=""
                      value={returnPostedDate}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">

                    <label className={`edit-payment-modal-label`} htmlFor="payment_return_code_dropdown_option_id">Payment Return Code:</label>
                    <Controller
                      name="payment_return_code_dropdown_option_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          name="payment_return_code_dropdown_option_id"
                          classNames={reactSelectClassNames()}
                          options={paymentReturnCodeOptions}
                          isSearchable={false}
                          isClearable
                          onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                          styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          menuPosition="fixed"
                          isDisabled={disableFields}
                        />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="check_number">Check Number:</label>
                    <Controller
                      name="check_number"
                      control={control}
                      render={({ field }) => (
                        <input {...field} data-testid="edit-payment-modal-check-number" type="text" id="check_number" className="input is-small" disabled={disableFields} />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="payment_status_dropdown_option_id">Payment Status:</label>
                    <Controller
                      name="payment_status_dropdown_option_id"
                      control={control}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          testId="edit-payment-modal-payment-status"
                          name="payment_status_dropdown_option_id"
                          classNames={reactSelectClassNames()}
                          options={paymentStatusOptions}
                          isSearchable={false}
                          onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                          styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          menuPosition="fixed"
                          isDisabled={disableFields}
                        />
                      )}
                    />
                  </div>

                  <div className="field is-horizontal edit-payment-modal-field">
                    <label className={`edit-payment-modal-label`} htmlFor="payment_method_dropdown_option_id">Payment
                      Method: <span
                        className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                        data-tooltip={paymentMethodHelperText}
                      >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span></label>
                    <Controller
                      name="payment_method_dropdown_option_id"
                      control={control}
                      render={({ field }) => (
                        <LoxSelect
                          {...field}
                          testId="edit-payment-modal-payment-method"
                          name="payment_method_dropdown_option_id"
                          classNames={reactSelectClassNames()}
                          options={availablePaymentMethods}
                          isSearchable={false}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value });
                            setDisableFields(dropdownOptions.find((o) => o?.id === val?.value && o.optional_value_3 === 'FALSE') ? false : true);
                          }}
                          styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          menuPosition="fixed"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button type="submit" data-testid="edit-payment-modal-submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Submitting' : 'Continue'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
